// Native (Capacitor Android) printing-config durability: the localStorage
// `printing-settings` payload is mirrored into Capacitor Preferences (native
// storage that survives a WebView data wipe) and restored when the store
// comes up EMPTY — existing local data always wins over a backup.
//
// Async by design, NOT at store init: Preferences.get returns a Promise
// while the store hydrates useLocalStorage synchronously at first access,
// and the scoped key needs restaurant/branch identity that only exists after
// the async restaurant bootstrap.
import { watch } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { Preferences } from '@capacitor/preferences';
import { Globals } from '@/modules/Globals';
import { PrintSource, TicketPrintSetting } from '@/enums/PrintEnum';
import {
  getDefaultPrintingSettings,
  INetworkPrinterConfig,
  IPersistedPrinterSettings,
  IPersistedPrintingSettings,
  usePrinting,
  usePrintingStore,
} from '@/composition/use-printing';
import { useRestaurantStore } from '@/stores/restaurant.store';
import {
  normalizeEscposSettings,
  normalizeZplSettings,
} from '@/lib/print/printer-settings';
import { IPrintingSettings } from '@/interfaces/IPrintingSettings';

export const PRINTING_BACKUP_VERSION = 1;

/** Settle time between a settings change and its Preferences write. */
const BACKUP_DEBOUNCE_MS = 5000;

export interface IPrintingBackupPayload {
  version: typeof PRINTING_BACKUP_VERSION;
  capturedAtMs: number;
  restaurantId: number;
  branchId: number;
  printingSettings: IPersistedPrintingSettings;
  /** Raw legacy localStorage `stickerSettings` string (verbatim mirror). */
  stickerSettings: string | null;
  /** Reserved (job history is device-local noise — never backed up). */
  jobsLog: null;
}

/** Restaurant+branch scoped key: the key itself is the isolation boundary. */
export function printingBackupKeyOf(
  restaurantId: number,
  branchId: number
): string {
  return `admin:printing:backup:v1:r${restaurantId}:b${branchId}`;
}

/**
 * "Meaningful" gate — shared by the backup writer (an empty payload must
 * never clobber a good backup) and the restore (an empty backup is not worth
 * applying): at least one printer (saved network config or per-printer
 * settings entry) or one assignment name.
 */
export function isMeaningfulPrintingSettings(
  settings: IPersistedPrintingSettings
): boolean {
  const hasPrinter =
    (settings.networkPrinters?.length ?? 0) > 0 ||
    Object.keys(settings.printerSettings ?? {}).length > 0;
  const hasAssignment =
    !!settings.ticketPrinterName ||
    !!settings.stickerPrinterName ||
    Object.values(settings.comandaPrinterNamesByStation ?? {}).some(
      (name) => !!name
    );
  return hasPrinter || hasAssignment;
}

/** Enum whitelists (numeric enums reverse-map, so filter to numbers). */
const TRIGGER_VALUES = new Set<number>(
  Object.values(TicketPrintSetting).filter((v) => typeof v === 'number')
);
const SOURCE_VALUES = new Set<number>(
  Object.values(PrintSource).filter((v) => typeof v === 'number')
);

/** Destination settings: known primitives only, whitelisted over defaults. */
function sanitizeDestinationSettings(raw: unknown): IPrintingSettings {
  const result = getDefaultPrintingSettings();
  if (!raw || typeof raw !== 'object') return result;
  const src = raw as Record<string, unknown>;
  if (typeof src.trigger === 'number' && TRIGGER_VALUES.has(src.trigger)) {
    result.trigger = src.trigger as TicketPrintSetting;
  }
  if (typeof src.source === 'number' && SOURCE_VALUES.has(src.source)) {
    result.source = src.source as PrintSource;
  }
  if (typeof src.encoding === 'string' && src.encoding) {
    // Codepage whitelist via the shared normalizer (junk → cp858).
    result.encoding = normalizeEscposSettings({
      codepage: src.encoding,
    }).codepage;
  }
  return result;
}

/**
 * Absence-preserving partial whitelist: only the keys present in the raw
 * partial survive, each carrying its value from the full-shape normalizer
 * run over that partial (junk values collapse to defaults, unknown keys are
 * dropped). Entries MUST stay Partial (IPersistedPrinterSettings contract):
 * read-time fallback chains only fire on ABSENCE — e.g. printerSettingsOf
 * falls back to the destination encoding only when the entry has no explicit
 * codepage — so materializing full defaults here would silently change what
 * a restored device prints.
 */
function sanitizePartialSettings<T extends object>(
  raw: object,
  normalized: T
): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(normalized) as (keyof T)[]) {
    if (key in raw) result[key] = normalized[key];
  }
  return result;
}

/**
 * Whitelists an untrusted backup payload into a valid
 * IPersistedPrintingSettings: every hardware field through the shared
 * normalizers (absence-preserving — see sanitizePartialSettings), malformed
 * network configs dropped. Assignment names are kept even when they don't
 * resolve within the backup itself: entry-less assigned usb/bt printers are
 * a legitimate steady state (a Preferences-tab assignment never creates a
 * printerSettings entry), and an unresolved name restores to exactly the
 * ghost state the store already handles (persisted-name retention +
 * re-resolution on discovery).
 */
export function sanitizeBackupPrintingSettings(
  raw: unknown
): IPersistedPrintingSettings {
  const src = (
    raw && typeof raw === 'object' ? raw : {}
  ) as Partial<IPersistedPrintingSettings>;

  const networkPrinters: INetworkPrinterConfig[] = [];
  for (const entry of Array.isArray(src.networkPrinters)
    ? src.networkPrinters
    : []) {
    if (!entry || typeof entry !== 'object') continue;
    const { name, host, port, supportsDleEot } =
      entry as Partial<INetworkPrinterConfig>;
    if (typeof name !== 'string' || !name.trim()) continue;
    if (typeof host !== 'string' || !host.trim()) continue;
    if (
      typeof port !== 'number' ||
      !Number.isFinite(port) ||
      port <= 0 ||
      port > 65535
    ) {
      continue;
    }
    if (networkPrinters.some((existing) => existing.name === name)) continue;
    networkPrinters.push({
      name,
      host,
      port: Math.floor(port),
      supportsDleEot: supportsDleEot === true,
    });
  }

  const printerSettings: Record<string, IPersistedPrinterSettings> = {};
  const rawSettings =
    src.printerSettings && typeof src.printerSettings === 'object'
      ? src.printerSettings
      : {};
  for (const [name, value] of Object.entries(rawSettings)) {
    if (!name || !value || typeof value !== 'object') continue;
    const entry = value as Partial<IPersistedPrinterSettings>;
    const type = entry.type === 'zpl' ? 'zpl' : 'escpos';
    const escposRaw =
      entry.escpos && typeof entry.escpos === 'object' ? entry.escpos : null;
    const zplRaw =
      entry.zpl && typeof entry.zpl === 'object' ? entry.zpl : null;
    printerSettings[name] = {
      type,
      ...(escposRaw
        ? {
            escpos: sanitizePartialSettings(
              escposRaw,
              normalizeEscposSettings(escposRaw)
            ),
          }
        : {}),
      ...(zplRaw
        ? { zpl: sanitizePartialSettings(zplRaw, normalizeZplSettings(zplRaw)) }
        : {}),
    };
  }

  const nameOrNull = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? value : null;

  const comandaPrinterNamesByStation: Record<number, string | null> = {};
  const rawByStation =
    src.comandaPrinterNamesByStation &&
    typeof src.comandaPrinterNamesByStation === 'object'
      ? src.comandaPrinterNamesByStation
      : {};
  for (const [stationKey, value] of Object.entries(rawByStation)) {
    const stationId = Number(stationKey);
    if (!Number.isInteger(stationId)) continue;
    comandaPrinterNamesByStation[stationId] = nameOrNull(value);
  }

  // Key order matches persist()'s construction order: the restore writes
  // this shape into persistedSettings, and persist()'s fixed-point guard is
  // JSON-STRING equality — same key order means the first watcher pass after
  // a restore sees no echo to write.
  return {
    stickerPrinterName: nameOrNull(src.stickerPrinterName),
    comandaPrinterNamesByStation,
    ticketPrinterName: nameOrNull(src.ticketPrinterName),
    stickerPrintingSettings: sanitizeDestinationSettings(
      src.stickerPrintingSettings
    ),
    comandaPrintingSettings: sanitizeDestinationSettings(
      src.comandaPrintingSettings
    ),
    ticketPrintingSettings: sanitizeDestinationSettings(
      src.ticketPrintingSettings
    ),
    networkPrinters,
    printerSettings,
  };
}

/**
 * "Local data exists" check for the restore gate: the persisted payload is
 * the boot-time source of truth, but a user can be creating config through
 * the settings modal RIGHT NOW (in-memory refs lead the persist watcher by a
 * flush) — both sides must count.
 */
function localHasMeaningfulSettings(
  printingStore: ReturnType<typeof usePrintingStore>
): boolean {
  if (
    isMeaningfulPrintingSettings(printingStore.persistedSettingsSnapshot())
  ) {
    return true;
  }
  return (
    printingStore.networkPrinters.length > 0 ||
    Object.keys(printingStore.printerSettings).length > 0 ||
    !!printingStore.ticketPrinter ||
    !!printingStore.stickerPrinter ||
    Object.values(printingStore.comandaPrintersByStation).some(
      (printer) => !!printer
    )
  );
}

/**
 * Applies the scoped backup when (and only when) the local store is empty.
 * Returns true when a restore was applied. Exposed for the e2e/unit
 * harnesses; production entry point is initPrintingBackup().
 */
export async function restorePrintingBackupIfEmpty(
  restaurantId: number,
  branchId: number
): Promise<boolean> {
  const printingStore = usePrintingStore();
  const printing = usePrinting();

  // Existing local data ALWAYS wins over a backup.
  if (localHasMeaningfulSettings(printingStore)) return false;

  const key = printingBackupKeyOf(restaurantId, branchId);
  const { value } = await Preferences.get({ key });
  if (!value) return false;

  let parsed: Partial<IPrintingBackupPayload> | null = null;
  try {
    parsed = JSON.parse(value) as Partial<IPrintingBackupPayload>;
  } catch (e) {
    console.warn('[PrintingBackup] unreadable backup payload', e);
    return false;
  }
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.version !== PRINTING_BACKUP_VERSION
  ) {
    return false;
  }

  if (parsed.restaurantId !== restaurantId || parsed.branchId !== branchId) {
    // The scoped key IS the isolation — a mismatched identity inside it is
    // suspicious but not a reason to lose a valid backup.
    console.warn(
      '[PrintingBackup] identity mismatch inside scoped key',
      key,
      parsed.restaurantId,
      parsed.branchId
    );
  }

  const sanitized = sanitizeBackupPrintingSettings(parsed.printingSettings);
  if (!isMeaningfulPrintingSettings(sanitized)) return false;

  // Re-check emptiness right before applying: the async Preferences read may
  // have raced the user creating config through the settings modal.
  if (localHasMeaningfulSettings(printingStore)) return false;

  printingStore.restorePersistedSettings(sanitized);
  if (typeof parsed.stickerSettings === 'string') {
    try {
      // Legacy mirror re-written verbatim: StickerPrintModal/StickerManager/
      // QrPage still read LabelPrinter.loadSettings() from this key.
      localStorage.setItem('stickerSettings', parsed.stickerSettings);
    } catch (e) {
      console.warn('[PrintingBackup] could not restore stickerSettings', e);
    }
  }
  printingStore.recomputeLegacyDestinationEncodings();

  // Re-resolve live printer refs from the restored names. Passive on
  // purpose: a boot-time restore must never raise the BT permission prompt.
  await printing.reloadAllPrinters({ interactive: false });

  console.log('[PrintingBackup] restored printing settings from backup', key);
  return true;
}

async function writeBackup(
  restaurantId: number,
  branchId: number
): Promise<void> {
  const printingStore = usePrintingStore();
  const snapshot = printingStore.persistedSettingsSnapshot();
  // An empty payload must never clobber a good backup (this is the whole
  // point of the feature: the empty state is what we recover FROM).
  if (!isMeaningfulPrintingSettings(snapshot)) return;

  const payload: IPrintingBackupPayload = {
    version: PRINTING_BACKUP_VERSION,
    capturedAtMs: Date.now(),
    restaurantId,
    branchId,
    printingSettings: snapshot,
    stickerSettings: localStorage.getItem('stickerSettings'),
    jobsLog: null,
  };
  try {
    await Preferences.set({
      key: printingBackupKeyOf(restaurantId, branchId),
      value: JSON.stringify(payload),
    });
  } catch (e) {
    console.error('[PrintingBackup] backup write failed', e);
  }
}

/** Idempotence guard (App bootstrap calls once; HMR/defensive re-calls no-op). */
let initialized = false;

/**
 * Production entry point, called from App bootstrap. Native-only. Waits for
 * restaurant/branch identity (the scoped key needs it), then restores-if-
 * empty ONCE and only afterwards starts the debounced backup watcher — a
 * boot-time write of the empty state must never race the restore read.
 */
export function initPrintingBackup(): void {
  if (initialized || !Globals.isCapacitorAndroid) return;
  initialized = true;

  const restaurantStore = useRestaurantStore();
  const printingStore = usePrintingStore();

  async function start(restaurantId: number, branchId: number): Promise<void> {
    try {
      await restorePrintingBackupIfEmpty(restaurantId, branchId);
    } catch (e) {
      console.error('[PrintingBackup] restore failed', e);
    }
    watchDebounced(
      // Snapshot getter as change source: the deep clone reads every nested
      // key, so ANY persisted change re-runs it (no deep flag needed).
      () => printingStore.persistedSettingsSnapshot(),
      () => void writeBackup(restaurantId, branchId),
      { debounce: BACKUP_DEBOUNCE_MS }
    );
    // Immediate first write so pre-existing configs become durable now, not
    // on their next edit (skip-empty guard applies inside).
    void writeBackup(restaurantId, branchId);
  }

  let started = false;
  // Assigned after watch() returns; the immediate invocation guards on null.
  let stopIdentityWatch: (() => void) | null = null;
  const onIdentity = (identity: {
    restaurantId: number | null;
    branchId: number | null;
  }) => {
    if (started || identity.restaurantId === null || identity.branchId === null)
      return;
    started = true;
    stopIdentityWatch?.();
    void start(identity.restaurantId, identity.branchId);
  };
  stopIdentityWatch = watch(
    () => ({
      restaurantId: restaurantStore.restaurant?.id ?? null,
      branchId: restaurantStore.selectedBranch?.id ?? null,
    }),
    onIdentity,
    { immediate: true }
  );
  // Identity was already available during the immediate call: the watcher
  // could not stop itself (handle not yet assigned) — stop it now.
  if (started) stopIdentityWatch();
}
